import { getFeatureDetails } from './getFeatureDetails';

import type { Feature, FeatureReturns } from './features';
import type { Attribute, TrafficType } from './types';
import type { SplitClient } from './useGetSplitClient/useGetSplitClient';

const NOT_READY_CONTROL_TREATMENT = {
  treatment: 'control',
  configJSON: null,
  isReady: false,
} as const;

type FeatureTreatmentWithConfigJSON<F extends Feature> = {
  treatment: FeatureReturns[F]['treatment'];
  configJSON: FeatureReturns[F]['config'] extends undefined ? null : string;
  isReady: true;
};

type ControlTreatmentWithConfigJSON = {
  treatment: 'control';
  configJSON: null;
  isReady: boolean;
};

type GetTreatmentWithConfigJSONReturn<F extends Feature> =
  | FeatureTreatmentWithConfigJSON<F>
  | ControlTreatmentWithConfigJSON;

export function getTreatmentWithConfigJSON<F extends Feature>(
  feature: F,
  getSplitClient: (trafficType: TrafficType) => SplitClient,
  getAttributesWithValues: (attributes: Set<Attribute>) => SplitIO.Attributes
): GetTreatmentWithConfigJSONReturn<F> {
  const { trafficType, attributes } = getFeatureDetails(feature);

  const { client, isReady } = getSplitClient(trafficType);

  if (!client || !isReady) {
    return NOT_READY_CONTROL_TREATMENT;
  }

  const attributesWithValues =
    attributes && getAttributesWithValues(attributes);

  const { treatment, config: configJSON } = client.getTreatmentWithConfig(
    feature,
    attributesWithValues
  ) as {
    treatment: FeatureReturns[F]['treatment'];
    config: FeatureReturns[F]['config'] extends undefined ? null : string;
  };

  return {
    treatment,
    configJSON,
    isReady,
  };
}
